import React, { useState, useEffect, useCallback } from 'react';
import {
  Calendar,
  Plus,
  Search,
  Edit2,
  Trash2,
  Clock,
  MapPin,
  AlertCircle,
  Check,
  Filter,
  Users as UsersIcon,
  Archive,
  ArchiveRestore,
} from 'lucide-react';
import { toast } from 'react-hot-toast';
import eventsService from '../../services/carehome_manager/eventsService';
import { Pagination, Modal, DataTable, Button, PageHeader, Select } from '../../components';

/**
 * Events page — list + add/edit modal.
 */

const initials = (name) => {
  if (!name) return '?';
  return name.trim().split(/\s+/).slice(0, 2).map((p) => p[0]?.toUpperCase()).join('') || '?';
};

// Visual styling per attendance status, including invitees who haven't replied.
const ATTEND_META = {
  attending: { label: 'Attending', chip: 'bg-emerald-50 dark:bg-emerald-900/10 text-emerald-700 dark:text-emerald-400 border-emerald-100 dark:border-emerald-900/50', dot: 'bg-emerald-500', mark: '✓' },
  maybe: { label: 'Maybe', chip: 'bg-amber-50 dark:bg-amber-900/10 text-amber-700 dark:text-amber-400 border-amber-100 dark:border-amber-900/50', dot: 'bg-amber-500', mark: '?' },
  not_attending: { label: 'Not attending', chip: 'bg-rose-50 dark:bg-red-900/10 text-rose-700 dark:text-red-400 border-rose-100 dark:border-red-900/50', dot: 'bg-rose-500', mark: '✕' },
  no_response: { label: 'Awaiting response', chip: 'bg-slate-50 dark:bg-[#31373f] text-slate-500 dark:text-gray-400 border-slate-200 dark:border-gray-700', dot: 'bg-slate-300', mark: '…' },
};
const ATTEND_ORDER = ['attending', 'maybe', 'not_attending', 'no_response'];

// Merge invited staff with their RSVP so every invitee shows a clear status.
const buildRoster = (event) => {
  const statusById = {};
  (event.attendance_list || []).forEach((a) => { statusById[a.id] = a.status; });
  const roster = (event.staff_attending_users || []).map((u) => ({
    id: u.id,
    name: u.name,
    status: statusById[u.id] || 'no_response',
  }));
  const invitedIds = new Set(roster.map((u) => u.id));
  (event.attendance_list || []).forEach((a) => {
    if (!invitedIds.has(a.id)) roster.push({ id: a.id, name: a.name, status: a.status });
  });
  return roster.sort(
    (a, b) => ATTEND_ORDER.indexOf(a.status) - ATTEND_ORDER.indexOf(b.status),
  );
};

const formatDate = (iso) => {
  if (!iso) return '—';
  const d = new Date(iso);
  return d.toLocaleDateString(undefined, { day: '2-digit', month: 'short', year: 'numeric' });
};

const formatTime = (time) => {
  if (!time) return '';
  return String(time).substring(0, 5);
};

const emptyForm = () => ({
  event_name: '',
  event_type_id: '',
  event_date: '',
  start_time: '',
  end_time: '',
  location: '',
  staff_attending: [],
  clients_attending: [],
  event_description: '',
  status: 'upcoming',
});

const getStatusColor = (status) => {
  switch (status) {
    case 'upcoming': return 'bg-blue-50 dark:bg-blue-900/20 text-blue-700 dark:text-blue-400 border-blue-100 dark:border-blue-900/50';
    case 'completed': return 'bg-green-50 dark:bg-emerald-900/10 text-green-700 dark:text-emerald-400 border-green-100 dark:border-emerald-900/50';
    case 'cancelled': return 'bg-red-50 dark:bg-red-900/10 text-red-700 dark:text-red-400 border-red-100 dark:border-red-900/50';
    default: return 'bg-gray-50 dark:bg-[#31373f] text-gray-700 dark:text-gray-200 border-gray-100 dark:border-gray-800';
  }
};

const Events = () => {
  const [events, setEvents] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [search, setSearch] = useState('');
  const [submittedSearch, setSubmittedSearch] = useState('');
  const [statusFilter, setStatusFilter] = useState('');
  const [tab, setTab] = useState('active'); // 'active' | 'archived'

  // Modal state
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [editingEvent, setEditingEvent] = useState(null);
  const [deleteConfirm, setDeleteConfirm] = useState(null);
  const [attendanceEvent, setAttendanceEvent] = useState(null);
  const [submitting, setSubmitting] = useState(false);

  // Form
  const [formData, setFormData] = useState(emptyForm());

  // Master + lookup data for the modal pickers
  const [options, setOptions] = useState({ types: [], staff: [], clients: [] });
  const [optionsLoaded, setOptionsLoaded] = useState(false);
  const [staffPickerOpen, setStaffPickerOpen] = useState(false);
  const [clientsPickerOpen, setClientsPickerOpen] = useState(false);

  const LIMIT = 10;

  const fetchEvents = useCallback(async (page = 1) => {
    try {
      setLoading(true);
      setError(null);
      const response = await eventsService.getAllEvents(page, LIMIT, submittedSearch, statusFilter, tab === 'archived');
      if (response.success) {
        setEvents(response.data || []);
        setTotalPages(response.pagination.pages || 1);
        setCurrentPage(response.pagination.page || 1);
      } else {
        setError(response.message || 'Failed to fetch events');
      }
    } catch (err) {
      setError(err.message || 'Error fetching events');
    } finally {
      setLoading(false);
    }
  }, [submittedSearch, statusFilter, tab]);

  useEffect(() => {
    fetchEvents(currentPage);
  }, [currentPage, fetchEvents]);

  const loadOptions = async () => {
    if (optionsLoaded) return;
    try {
      const res = await eventsService.getEventOptions();
      if (res.success) {
        setOptions(res.data);
        setOptionsLoaded(true);
      }
    } catch (err) {
      toast.error('Could not load event options');
    }
  };

  const handleSearch = (e) => {
    e?.preventDefault();
    setSubmittedSearch(search);
    setCurrentPage(1);
  };

  const handleOpenModal = (event = null) => {
    loadOptions();
    if (event) {
      setEditingEvent(event);
      setFormData({
        event_name: event.event_name || '',
        event_type_id: event.event_type_id || '',
        event_date: event.event_date
          ? String(event.event_date).substring(0, 10)
          : (event.event_datetime ? new Date(event.event_datetime).toISOString().substring(0, 10) : ''),
        start_time: formatTime(event.start_time)
          || (event.event_datetime ? new Date(event.event_datetime).toISOString().substring(11, 16) : ''),
        end_time: formatTime(event.end_time) || '',
        location: event.location || '',
        staff_attending: Array.isArray(event.staff_attending)
          ? event.staff_attending.map(Number)
          : [],
        clients_attending: Array.isArray(event.clients_attending)
          ? event.clients_attending.map(Number)
          : [],
        event_description: event.event_description || '',
        status: event.status || 'upcoming',
      });
    } else {
      setEditingEvent(null);
      setFormData(emptyForm());
    }
    setIsModalOpen(true);
  };

  const handleSubmit = async (e) => {
    e?.preventDefault?.();
    if (!formData.event_name.trim()) {
      toast.error('Event title is required');
      return;
    }
    if (!formData.event_type_id) {
      toast.error('Pick an event type');
      return;
    }
    if (!formData.event_date) {
      toast.error('Pick a date');
      return;
    }
    try {
      setSubmitting(true);
      const payload = {
        ...formData,
        event_type_id: Number(formData.event_type_id),
      };
      const response = editingEvent
        ? await eventsService.updateEvent(editingEvent.id, payload)
        : await eventsService.createEvent(payload);

      if (response.success) {
        toast.success(editingEvent ? 'Event updated successfully' : 'Event created successfully');
        setIsModalOpen(false);
        fetchEvents(currentPage);
      }
    } catch (err) {
      toast.error(err.message || 'Error saving event');
    } finally {
      setSubmitting(false);
    }
  };

  const handleDelete = async () => {
    if (!deleteConfirm) return;
    try {
      setSubmitting(true);
      const response = await eventsService.deleteEvent(deleteConfirm.id);
      if (response.success) {
        toast.success('Event deleted successfully');
        setDeleteConfirm(null);
        fetchEvents(currentPage);
      }
    } catch (err) {
      toast.error(err.message || 'Error deleting event');
    } finally {
      setSubmitting(false);
    }
  };

  const handleToggleArchive = async (event) => {
    try {
      await eventsService.toggleArchiveEvent(event.id);
      toast.success(event.is_archived ? 'Event restored' : 'Event archived');
      fetchEvents(currentPage);
    } catch (err) {
      toast.error(err.message || 'Error updating event');
    }
  };

  const toggleStaff = (id) => {
    setFormData((f) => {
      const exists = f.staff_attending.includes(id);
      return { ...f, staff_attending: exists ? f.staff_attending.filter((x) => x !== id) : [...f.staff_attending, id] };
    });
  };

  const toggleClient = (id) => {
    setFormData((f) => {
      const exists = f.clients_attending.includes(id);
      return { ...f, clients_attending: exists ? f.clients_attending.filter((x) => x !== id) : [...f.clients_attending, id] };
    });
  };

  const toggleSelectAll = (field, allIds) => {
    setFormData((f) => {
      const current = f[field] || [];
      const allSelected = allIds.length > 0 && allIds.every((id) => current.includes(id));
      return { ...f, [field]: allSelected ? [] : [...allIds] };
    });
  };

  const staffNameById = options.staff.reduce((acc, s) => { acc[s.id] = s.name; return acc; }, {});
  const selectedStaffNames = formData.staff_attending.map((id) => staffNameById[id]).filter(Boolean);

  // Empty-state messaging (filter-aware) for the table.
  const eventsEmptyMsg = tab === 'archived'
    ? 'No archived events. Archive an event from the Active tab to see it here.'
    : (submittedSearch || statusFilter)
      ? "We couldn't find any events matching your current filters."
      : "You haven't added any events yet. Click 'Add New Event' to get started.";
  const eventsShowClear = submittedSearch || statusFilter;
  const clearEventsFilters = () => { setSearch(''); setSubmittedSearch(''); setStatusFilter(''); };

  // Table columns — same cells as before, driven by DataTable.
  const eventsColumns = [
    {
      key: 'event_name', header: 'Event',
      render: (event) => (
        <div>
          <div className="text-[14px] font-semibold text-[#1E293B] dark:text-gray-100">{event.event_name}</div>
          <div className="text-[12px] text-[#64748B] dark:text-gray-400 line-clamp-1 max-w-[300px] mt-0.5">{event.event_description || 'No notes'}</div>
        </div>
      ),
    },
    {
      key: 'type', header: 'Type',
      render: (event) => (
        <span className="text-[12px] font-bold text-[#475569] dark:text-gray-400 bg-[#F1F5F9] dark:bg-[#31373f] rounded-md px-2 py-1">{event.eventType?.name || '—'}</span>
      ),
    },
    {
      key: 'when', header: 'When',
      render: (event) => (
        <div className="flex flex-col gap-0.5">
          <span className="flex items-center gap-1.5 text-[13px] text-[#1E293B] dark:text-gray-200 font-medium">
            <Calendar className="w-3.5 h-3.5 text-[#94A3B8]" />
            {formatDate(event.event_date || event.event_datetime)}
          </span>
          {(event.start_time || event.end_time) && (
            <span className="flex items-center gap-1.5 text-[11px] text-[#64748B] dark:text-gray-400">
              <Clock className="w-3 h-3" />
              {formatTime(event.start_time) || '—'}{event.end_time ? ` – ${formatTime(event.end_time)}` : ''}
            </span>
          )}
        </div>
      ),
    },
    {
      key: 'location', header: 'Location',
      render: (event) => (event.location ? (
        <span className="flex items-center gap-1.5 text-[12px] text-[#475569] dark:text-gray-400">
          <MapPin className="w-3.5 h-3.5 text-[#94A3B8]" />
          {event.location}
        </span>
      ) : (
        <span className="text-[11px] text-[#94A3B8] dark:text-gray-500 italic">Not set</span>
      )),
    },
    {
      key: 'attending', header: 'Attending',
      render: (event) => {
        const roster = buildRoster(event);
        const rsvp = event.attendance_summary || { attending: 0, not_attending: 0, maybe: 0 };
        const noResponse = roster.filter((u) => u.status === 'no_response').length;
        if (roster.length === 0) return <span className="text-[11px] text-[#94A3B8] dark:text-gray-500 italic">No one invited</span>;
        return (
          <button
            type="button"
            onClick={(e) => { e.stopPropagation(); setAttendanceEvent(event); }}
            className="flex flex-col items-start gap-2 text-left"
            title="View attendance list"
          >
            <div className="flex -space-x-1.5">
              {roster.slice(0, 3).map((u) => (
                <div
                  key={u.id}
                  title={`${u.name} — ${ATTEND_META[u.status].label}`}
                  className="w-7 h-7 rounded-full bg-blue-100 text-[#2563EB] text-[10px] font-black flex items-center justify-center border-2 border-white"
                >
                  {initials(u.name)}
                </div>
              ))}
              {roster.length > 3 && (
                <div className="w-7 h-7 rounded-full bg-gray-100 dark:bg-[#31373f] text-[#64748B] dark:text-gray-400 text-[10px] font-black flex items-center justify-center border-2 border-white">
                  +{roster.length - 3}
                </div>
              )}
            </div>
            <div className="flex items-center gap-1.5 flex-wrap">
              {rsvp.attending > 0 && <span className={`inline-flex items-center gap-1 text-[10px] font-bold px-1.5 py-0.5 rounded border ${ATTEND_META.attending.chip}`}>✓ {rsvp.attending}</span>}
              {rsvp.maybe > 0 && <span className={`inline-flex items-center gap-1 text-[10px] font-bold px-1.5 py-0.5 rounded border ${ATTEND_META.maybe.chip}`}>? {rsvp.maybe}</span>}
              {rsvp.not_attending > 0 && <span className={`inline-flex items-center gap-1 text-[10px] font-bold px-1.5 py-0.5 rounded border ${ATTEND_META.not_attending.chip}`}>✕ {rsvp.not_attending}</span>}
              {noResponse > 0 && <span className={`inline-flex items-center gap-1 text-[10px] font-bold px-1.5 py-0.5 rounded border ${ATTEND_META.no_response.chip}`}>… {noResponse}</span>}
            </div>
          </button>
        );
      },
    },
    {
      key: 'status', header: 'Status',
      render: (event) => (
        <span className={`inline-flex items-center px-2.5 py-1 rounded-md text-[11px] font-bold uppercase tracking-wider border ${getStatusColor(event.status)}`}>
          {event.status}
        </span>
      ),
    },
    {
      key: 'actions', header: 'Actions', align: 'right',
      render: (event) => (
        <div className="flex items-center justify-end gap-2">
          <button onClick={() => handleOpenModal(event)} className="p-1.5 rounded-lg text-[#64748B] dark:text-gray-400 hover:text-[#2563EB] hover:bg-blue-50 transition-colors" title="Edit">
            <Edit2 className="w-4 h-4" />
          </button>
          <button onClick={() => handleToggleArchive(event)} className="p-1.5 rounded-lg text-[#64748B] dark:text-gray-400 hover:text-amber-600 hover:bg-amber-50 transition-colors" title={event.is_archived ? 'Restore' : 'Archive'}>
            {event.is_archived ? <ArchiveRestore className="w-4 h-4" /> : <Archive className="w-4 h-4" />}
          </button>
          <button onClick={() => setDeleteConfirm(event)} className="p-1.5 rounded-lg text-[#64748B] dark:text-gray-400 hover:text-red-600 hover:bg-red-50 transition-colors" title="Delete">
            <Trash2 className="w-4 h-4" />
          </button>
        </div>
      ),
    },
  ];

  return (
    <div className="space-y-6 pb-8">
      {/* Header */}
      <PageHeader
        title="Events Management"
        subtitle="Schedule and manage your care home activities and events."
        actions={(
          <Button variant="primary" leftIcon={<Plus className="w-5 h-5" />} onClick={() => handleOpenModal()}>
            Add New Event
          </Button>
        )}
      />

      <div className="bg-white dark:bg-[#262b33] border border-[#E2E8F0] dark:border-gray-800 rounded-2xl shadow-sm overflow-hidden">
        {/* Toolbar */}
        <div className="p-4 border-b border-[#E2E8F0] dark:border-gray-800 bg-white dark:bg-[#262b33]">
          <div className="flex flex-wrap items-center gap-3">
            {/* Active / Archived tabs */}
            <div className="flex items-center gap-1 p-1 bg-[#F1F5F9] dark:bg-[#31373f] rounded-xl">
              {[
                { key: 'active', label: 'Active' },
                { key: 'archived', label: 'Archived' },
              ].map((t) => (
                <button
                  key={t.key}
                  type="button"
                  onClick={() => { setTab(t.key); setCurrentPage(1); }}
                  className={`px-3 py-1.5 rounded-lg text-[13px] font-semibold transition-colors ${
                    tab === t.key ? 'bg-white dark:bg-[#262b33] text-[#2563EB] dark:text-blue-400 shadow-sm' : 'text-[#64748B] dark:text-gray-400 hover:text-[#1E293B] dark:hover:text-gray-200'
                  }`}
                >
                  {t.label}
                </button>
              ))}
            </div>

            <Select
              className="w-[170px]"
              leftIcon={<Filter className="w-4 h-4" />}
              value={statusFilter}
              onChange={(v) => { setStatusFilter(v); setCurrentPage(1); }}
              options={[
                { value: '', label: 'All Statuses' },
                { value: 'upcoming', label: 'Upcoming' },
                { value: 'completed', label: 'Completed' },
                { value: 'cancelled', label: 'Cancelled' },
              ]}
            />

            <form onSubmit={handleSearch} className="flex items-center gap-2 flex-grow sm:flex-grow-0">
              <div className="relative w-full sm:w-[320px]">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-[#94A3B8]" />
                <input
                  type="text"
                  placeholder="Search events..."
                  value={search}
                  onChange={(e) => setSearch(e.target.value)}
                  className="w-full pl-9 pr-4 py-2 border border-[#CBD5E1] dark:border-gray-700 rounded-xl focus:outline-none focus:ring-2 focus:ring-[#2563EB]/20 focus:border-[#2563EB] text-[13px] text-[#0F172A] dark:text-gray-200 transition-colors bg-[#F8FAFC] dark:bg-[#31373f]"
                />
              </div>
              <Button type="submit" variant="primary" icon={<Search className="w-4 h-4" />} aria-label="Search" />
            </form>
          </div>
        </div>

        {/* List */}
        {error ? (
          <div className="p-12 text-center text-red-500">
            <AlertCircle className="w-12 h-12 mx-auto mb-4 opacity-20" />
            <p>{error}</p>
            <button onClick={() => fetchEvents(currentPage)} className="mt-4 text-sm text-blue-600 hover:underline">Try Again</button>
          </div>
        ) : (
          <DataTable
            bare
            columns={eventsColumns}
            data={events}
            loading={loading}
            rowKey={(e) => e.id}
            emptyIcon={Calendar}
            emptyTitle="No events found"
            emptyText={eventsEmptyMsg}
            emptyAction={eventsShowClear ? (
              <Button variant="ghost" size="sm" onClick={clearEventsFilters}>Clear filters</Button>
            ) : undefined}
          />
        )}

        {totalPages > 1 && (
          <div className="p-4 border-t border-[#E2E8F0] dark:border-gray-800">
            <Pagination currentPage={currentPage} totalPages={totalPages} onPageChange={setCurrentPage} />
          </div>
        )}
      </div>

      {/* Add/Edit modal */}
      <Modal
        isOpen={isModalOpen}
        onClose={() => setIsModalOpen(false)}
        title={editingEvent ? 'Edit Event' : 'Add Event'}
        maxWidth="max-w-[560px]"
      >
        <form onSubmit={handleSubmit} className="space-y-5">
          {/* Title */}
          <div>
            <label className="block text-[12px] font-bold text-[#1E293B] dark:text-gray-200 mb-1.5">
              Event title <span className="text-rose-500">*</span>
            </label>
            <input
              type="text"
              value={formData.event_name}
              onChange={(e) => setFormData({ ...formData, event_name: e.target.value })}
              placeholder="e.g. Care plan review: John Doe"
              className="w-full px-3 py-2.5 rounded-xl border border-[#E2E8F0] dark:border-gray-700 dark:bg-[#31373f] dark:text-gray-200 focus:border-blue-500 focus:ring-2 focus:ring-blue-500/10 outline-none text-[13px] font-medium"
            />
          </div>

          {/* Type + Date */}
          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="block text-[12px] font-bold text-[#1E293B] dark:text-gray-200 mb-1.5">
                Type <span className="text-rose-500">*</span>
              </label>
              <Select
                className="w-full"
                value={formData.event_type_id}
                onChange={(v) => setFormData({ ...formData, event_type_id: v })}
                placeholder="Select type"
                options={options.types.map((t) => ({ value: String(t.id), label: t.name }))}
              />
            </div>
            <div>
              <label className="block text-[12px] font-bold text-[#1E293B] dark:text-gray-200 mb-1.5">
                Date <span className="text-rose-500">*</span>
              </label>
              <input
                type="date"
                value={formData.event_date}
                onChange={(e) => setFormData({ ...formData, event_date: e.target.value })}
                className="w-full px-3 py-2.5 rounded-xl border border-[#E2E8F0] dark:border-gray-700 dark:bg-[#31373f] dark:text-gray-200 focus:border-blue-500 focus:ring-2 focus:ring-blue-500/10 outline-none text-[13px] font-medium"
              />
            </div>
          </div>

          {/* Start / End time */}
          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="block text-[12px] font-bold text-[#1E293B] dark:text-gray-200 mb-1.5">Start time</label>
              <input
                type="time"
                value={formData.start_time}
                onChange={(e) => setFormData({ ...formData, start_time: e.target.value })}
                className="w-full px-3 py-2.5 rounded-xl border border-[#E2E8F0] dark:border-gray-700 dark:bg-[#31373f] dark:text-gray-200 focus:border-blue-500 focus:ring-2 focus:ring-blue-500/10 outline-none text-[13px] font-medium"
              />
            </div>
            <div>
              <label className="block text-[12px] font-bold text-[#1E293B] dark:text-gray-200 mb-1.5">End time</label>
              <input
                type="time"
                value={formData.end_time}
                onChange={(e) => setFormData({ ...formData, end_time: e.target.value })}
                className="w-full px-3 py-2.5 rounded-xl border border-[#E2E8F0] dark:border-gray-700 dark:bg-[#31373f] dark:text-gray-200 focus:border-blue-500 focus:ring-2 focus:ring-blue-500/10 outline-none text-[13px] font-medium"
              />
            </div>
          </div>

          {/* Location */}
          <div>
            <label className="block text-[12px] font-bold text-[#1E293B] dark:text-gray-200 mb-1.5">Location</label>
            <input
              type="text"
              value={formData.location}
              onChange={(e) => setFormData({ ...formData, location: e.target.value })}
              placeholder="Office / Client home / Video call"
              className="w-full px-3 py-2.5 rounded-xl border border-[#E2E8F0] dark:border-gray-700 dark:bg-[#31373f] dark:text-gray-200 focus:border-blue-500 focus:ring-2 focus:ring-blue-500/10 outline-none text-[13px] font-medium"
            />
          </div>

          {/* Staff attending */}
          <div>
            <label className="block text-[12px] font-bold text-[#1E293B] dark:text-gray-200 mb-1.5">Staff attending</label>
            <button
              type="button"
              onClick={() => setStaffPickerOpen((v) => !v)}
              className="w-full flex items-center justify-between gap-2 px-3 py-2.5 rounded-xl border border-[#E2E8F0] dark:border-gray-700 bg-white dark:bg-[#31373f] hover:border-blue-300 transition outline-none text-[13px]"
            >
              <div className="flex items-center gap-2 min-w-0">
                <UsersIcon className="w-4 h-4 text-[#94A3B8]" />
                {selectedStaffNames.length === 0 ? (
                  <span className="text-[#94A3B8] dark:text-gray-500">Click to add staff…</span>
                ) : (
                  <div className="flex flex-wrap gap-1 min-w-0">
                    {selectedStaffNames.slice(0, 3).map((n, i) => (
                      <span key={i} className="inline-flex items-center gap-1 px-2 py-0.5 rounded-md bg-blue-50 dark:bg-blue-900/20 text-[#2563EB] dark:text-blue-400 text-[11px] font-bold">
                        {n}
                      </span>
                    ))}
                    {selectedStaffNames.length > 3 && (
                      <span className="text-[11px] font-bold text-[#64748B] dark:text-gray-400">+{selectedStaffNames.length - 3}</span>
                    )}
                  </div>
                )}
              </div>
              <span className="text-[#94A3B8] text-xs">{staffPickerOpen ? 'Done' : 'Choose'}</span>
            </button>

            {staffPickerOpen && (
              <div className="mt-2 max-h-[220px] overflow-y-auto border border-[#E2E8F0] dark:border-gray-800 rounded-xl p-1 bg-white dark:bg-[#262b33] shadow-sm">
                {options.staff.length === 0 ? (
                  <p className="text-[11.5px] text-[#94A3B8] dark:text-gray-500 italic p-3">No staff available.</p>
                ) : (
                  <>
                    {(() => {
                      const allIds = options.staff.map((u) => u.id);
                      const allSelected = allIds.length > 0 && allIds.every((id) => formData.staff_attending.includes(id));
                      return (
                        <button
                          type="button"
                          onClick={() => toggleSelectAll('staff_attending', allIds)}
                          className={`w-full flex items-center gap-3 p-2.5 rounded-lg transition border-b border-[#F1F5F9] dark:border-gray-800 ${allSelected ? 'bg-blue-50 dark:bg-blue-500/15' : 'hover:bg-gray-50 dark:hover:bg-[#31373f]'}`}
                        >
                          <div className={`w-5 h-5 rounded border-2 flex items-center justify-center shrink-0 ${allSelected ? 'border-[#2563EB] bg-[#2563EB]' : 'border-[#E2E8F0] dark:border-gray-600'}`}>
                            {allSelected && <Check className="w-3 h-3 text-white" strokeWidth={3} />}
                          </div>
                          <span className="text-[12.5px] font-black uppercase tracking-wide text-[#2563EB] dark:text-blue-400 text-left flex-1">
                            Select all ({options.staff.length})
                          </span>
                        </button>
                      );
                    })()}
                    {options.staff.map((u) => {
                      const active = formData.staff_attending.includes(u.id);
                      return (
                        <button
                          key={u.id}
                          type="button"
                          onClick={() => toggleStaff(u.id)}
                          className={`w-full flex items-center gap-3 p-2.5 rounded-lg transition ${active ? 'bg-blue-50 dark:bg-blue-500/15' : 'hover:bg-gray-50 dark:hover:bg-[#31373f]'}`}
                        >
                          <div className={`w-5 h-5 rounded border-2 flex items-center justify-center shrink-0 ${active ? 'border-[#2563EB] bg-[#2563EB]' : 'border-[#E2E8F0] dark:border-gray-600'}`}>
                            {active && <Check className="w-3 h-3 text-white" strokeWidth={3} />}
                          </div>
                          <div className="w-7 h-7 rounded-full bg-blue-100 dark:bg-blue-900/40 text-[#2563EB] dark:text-blue-300 text-[9px] font-black flex items-center justify-center shrink-0">
                            {initials(u.name)}
                          </div>
                          <span className="text-[12.5px] font-semibold text-[#1E293B] dark:text-gray-200 text-left flex-1 truncate">
                            {u.name}
                          </span>
                        </button>
                      );
                    })}
                  </>
                )}
              </div>
            )}
          </div>

          {/* Client Details — multi-select with the same Select All UX as Staff */}
          <div>
            <label className="block text-[12px] font-bold text-[#1E293B] dark:text-gray-200 mb-1.5">Client Details</label>
            {(() => {
              const clientNameById = options.clients.reduce((acc, c) => { acc[c.id] = c.name; return acc; }, {});
              const selectedClientNames = formData.clients_attending.map((id) => clientNameById[id]).filter(Boolean);
              return (
                <>
                  <button
                    type="button"
                    onClick={() => setClientsPickerOpen((v) => !v)}
                    className="w-full flex items-center justify-between gap-2 px-3 py-2.5 rounded-xl border border-[#E2E8F0] dark:border-gray-700 bg-white dark:bg-[#31373f] hover:border-blue-300 transition outline-none text-[13px]"
                  >
                    <div className="flex items-center gap-2 min-w-0">
                      <UsersIcon className="w-4 h-4 text-[#94A3B8]" />
                      {selectedClientNames.length === 0 ? (
                        <span className="text-[#94A3B8] dark:text-gray-500">Click to add clients…</span>
                      ) : (
                        <div className="flex flex-wrap gap-1 min-w-0">
                          {selectedClientNames.slice(0, 3).map((n, i) => (
                            <span key={i} className="inline-flex items-center gap-1 px-2 py-0.5 rounded-md bg-emerald-50 dark:bg-emerald-900/10 text-emerald-700 dark:text-emerald-400 text-[11px] font-bold">
                              {n}
                            </span>
                          ))}
                          {selectedClientNames.length > 3 && (
                            <span className="text-[11px] font-bold text-[#64748B] dark:text-gray-400">+{selectedClientNames.length - 3}</span>
                          )}
                        </div>
                      )}
                    </div>
                    <span className="text-[#94A3B8] text-xs">{clientsPickerOpen ? 'Done' : 'Choose'}</span>
                  </button>

                  {clientsPickerOpen && (
                    <div className="mt-2 max-h-[220px] overflow-y-auto border border-[#E2E8F0] dark:border-gray-800 rounded-xl p-1 bg-white dark:bg-[#262b33] shadow-sm">
                      {options.clients.length === 0 ? (
                        <p className="text-[11.5px] text-[#94A3B8] dark:text-gray-500 italic p-3">No clients available.</p>
                      ) : (
                        <>
                          {(() => {
                            const allIds = options.clients.map((c) => c.id);
                            const allSelected = allIds.length > 0 && allIds.every((id) => formData.clients_attending.includes(id));
                            return (
                              <button
                                type="button"
                                onClick={() => toggleSelectAll('clients_attending', allIds)}
                                className={`w-full flex items-center gap-3 p-2.5 rounded-lg transition border-b border-[#F1F5F9] dark:border-gray-800 ${allSelected ? 'bg-emerald-50 dark:bg-emerald-500/15' : 'hover:bg-gray-50 dark:hover:bg-[#31373f]'}`}
                              >
                                <div className={`w-5 h-5 rounded border-2 flex items-center justify-center shrink-0 ${allSelected ? 'border-emerald-600 bg-emerald-600' : 'border-[#E2E8F0] dark:border-gray-600'}`}>
                                  {allSelected && <Check className="w-3 h-3 text-white" strokeWidth={3} />}
                                </div>
                                <span className="text-[12.5px] font-black uppercase tracking-wide text-emerald-700 dark:text-emerald-400 text-left flex-1">
                                  Select all ({options.clients.length})
                                </span>
                              </button>
                            );
                          })()}
                          {options.clients.map((c) => {
                            const active = formData.clients_attending.includes(c.id);
                            return (
                              <button
                                key={c.id}
                                type="button"
                                onClick={() => toggleClient(c.id)}
                                className={`w-full flex items-center gap-3 p-2.5 rounded-lg transition ${active ? 'bg-emerald-50 dark:bg-emerald-500/15' : 'hover:bg-gray-50 dark:hover:bg-[#31373f]'}`}
                              >
                                <div className={`w-5 h-5 rounded border-2 flex items-center justify-center shrink-0 ${active ? 'border-emerald-600 bg-emerald-600' : 'border-[#E2E8F0] dark:border-gray-600'}`}>
                                  {active && <Check className="w-3 h-3 text-white" strokeWidth={3} />}
                                </div>
                                <div className="w-7 h-7 rounded-full bg-emerald-100 dark:bg-emerald-900/40 text-emerald-700 dark:text-emerald-300 text-[9px] font-black flex items-center justify-center shrink-0">
                                  {initials(c.name)}
                                </div>
                                <span className="text-[12.5px] font-semibold text-[#1E293B] dark:text-gray-200 text-left flex-1 truncate">
                                  {c.name}
                                </span>
                              </button>
                            );
                          })}
                        </>
                      )}
                    </div>
                  )}
                </>
              );
            })()}
          </div>

          {/* Notes */}
          <div>
            <label className="block text-[12px] font-bold text-[#1E293B] dark:text-gray-200 mb-1.5">Notes</label>
            <textarea
              rows={3}
              value={formData.event_description}
              onChange={(e) => setFormData({ ...formData, event_description: e.target.value })}
              placeholder="Agenda or context…"
              className="w-full px-3 py-2.5 rounded-xl border border-[#E2E8F0] dark:border-gray-700 dark:bg-[#31373f] dark:text-gray-200 focus:border-blue-500 focus:ring-2 focus:ring-blue-500/10 outline-none text-[12.5px] resize-none"
            />
          </div>

          {/* Status — only shown when editing */}
          {editingEvent && (
            <div>
              <label className="block text-[12px] font-bold text-[#1E293B] dark:text-gray-200 mb-1.5">Status</label>
              <Select
                className="w-full"
                value={formData.status}
                onChange={(v) => setFormData({ ...formData, status: v })}
                options={[
                  { value: 'upcoming', label: 'Upcoming' },
                  { value: 'completed', label: 'Completed' },
                  { value: 'cancelled', label: 'Cancelled' },
                ]}
              />
            </div>
          )}

          {/* Footer */}
          <div className="flex items-center justify-end gap-2 pt-4 border-t border-[#F1F5F9] dark:border-gray-800">
            <Button type="button" variant="secondary" onClick={() => setIsModalOpen(false)}>Cancel</Button>
            <Button type="submit" variant="primary" loading={submitting}>
              {submitting
                ? (editingEvent ? 'Updating…' : 'Saving…')
                : (editingEvent ? 'Update event' : 'Save event')}
            </Button>
          </div>
        </form>
      </Modal>

      {/* Delete confirm */}
      <Modal
        isOpen={!!deleteConfirm}
        onClose={() => setDeleteConfirm(null)}
        title="Delete Event"
        maxWidth="max-w-[420px]"
      >
        <div className="space-y-6">
          <div className="flex flex-col items-center text-center">
            <div className="w-16 h-16 bg-red-50 dark:bg-red-900/10 rounded-full flex items-center justify-center mb-4">
              <Trash2 className="w-8 h-8 text-red-600" />
            </div>
            <h3 className="text-lg font-bold text-[#0F172A] dark:text-gray-100">Confirm Deletion</h3>
            <p className="text-[14px] text-[#64748B] dark:text-gray-400 mt-2">
              Are you sure you want to delete <span className="font-semibold text-[#0F172A] dark:text-gray-100">"{deleteConfirm?.event_name}"</span>? This action cannot be undone.
            </p>
          </div>
          <div className="flex items-center justify-center gap-3">
            <Button variant="ghost" onClick={() => setDeleteConfirm(null)}>Keep Event</Button>
            <Button variant="danger" onClick={handleDelete} loading={submitting}>
              {submitting ? 'Deleting…' : 'Delete Permanently'}
            </Button>
          </div>
        </div>
      </Modal>

      {/* Attendance roster */}
      <Modal
        isOpen={!!attendanceEvent}
        onClose={() => setAttendanceEvent(null)}
        title="Attendance"
        maxWidth="max-w-[480px]"
      >
        {attendanceEvent && (() => {
          const roster = buildRoster(attendanceEvent);
          const counts = roster.reduce((acc, u) => {
            acc[u.status] = (acc[u.status] || 0) + 1;
            return acc;
          }, {});
          return (
            <div className="space-y-5">
              <div>
                <div className="text-[15px] font-bold text-[#0F172A] dark:text-gray-100">{attendanceEvent.event_name}</div>
                <div className="text-[12px] text-[#64748B] dark:text-gray-400 mt-0.5">
                  {formatDate(attendanceEvent.event_date || attendanceEvent.event_datetime)}
                  {' · '}{roster.length} invited
                </div>
              </div>

              <div className="flex flex-wrap items-center gap-2">
                {ATTEND_ORDER.filter((s) => counts[s]).map((s) => (
                  <span key={s} className={`inline-flex items-center gap-1.5 text-[12px] font-semibold px-2.5 py-1 rounded-lg border ${ATTEND_META[s].chip}`}>
                    <span className={`w-1.5 h-1.5 rounded-full ${ATTEND_META[s].dot}`} />
                    {counts[s]} {ATTEND_META[s].label}
                  </span>
                ))}
              </div>

              {roster.length === 0 ? (
                <div className="text-[13px] text-[#94A3B8] dark:text-gray-500 italic py-6 text-center">No staff invited to this event.</div>
              ) : (
                <div className="border border-[#E2E8F0] dark:border-gray-800 rounded-xl divide-y divide-[#E2E8F0]/70 dark:divide-gray-800 max-h-[340px] overflow-y-auto">
                  {roster.map((u) => {
                    const meta = ATTEND_META[u.status];
                    return (
                      <div key={u.id} className="flex items-center justify-between gap-3 px-4 py-2.5">
                        <div className="flex items-center gap-3 min-w-0">
                          <div className="w-8 h-8 shrink-0 rounded-full bg-blue-100 text-[#2563EB] text-[11px] font-black flex items-center justify-center">
                            {initials(u.name)}
                          </div>
                          <span className="text-[13px] font-medium text-[#1E293B] dark:text-gray-200 truncate">{u.name}</span>
                        </div>
                        <span className={`inline-flex items-center gap-1.5 text-[11px] font-bold px-2 py-1 rounded-md border whitespace-nowrap ${meta.chip}`}>
                          <span className={`w-1.5 h-1.5 rounded-full ${meta.dot}`} />
                          {meta.label}
                        </span>
                      </div>
                    );
                  })}
                </div>
              )}
            </div>
          );
        })()}
      </Modal>
    </div>
  );
};

export default Events;
